import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { ProductCard } from '../components/ProductCard';
import { dashboardAPI } from '../utils/api';
import { Package, AlertTriangle, MessageSquare, TrendingUp, FileText, Upload, Plus } from 'lucide-react';
import { toast } from 'sonner';

export const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await dashboardAPI.getAdminDashboard();
      setDashboardData(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-consortium-green"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-barlow text-4xl font-bold text-slate-900 mb-2" data-testid="admin-dashboard-title">
            Admin Dashboard
          </h1>
          <p className="text-slate-600">Welcome back, {user?.name}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid="stat-total-products">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-consortium-green-light rounded-lg flex items-center justify-center">
                <Package className="w-6 h-6 text-consortium-green" />
              </div>
              <span className="text-3xl font-bold text-slate-900">{dashboardData?.total_products || 0}</span>
            </div>
            <p className="text-slate-600 font-medium">Total Products</p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid="stat-low-stock">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
              </div>
              <span className="text-3xl font-bold text-slate-900">{dashboardData?.low_stock_count || 0}</span>
            </div>
            <p className="text-slate-600 font-medium">Low Stock Items</p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid="stat-unanswered">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-blue-600" />
              </div>
              <span className="text-3xl font-bold text-slate-900">{dashboardData?.unanswered_questions || 0}</span>
            </div>
            <p className="text-slate-600 font-medium">Pending Questions</p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid="stat-performance">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-consortium-gold-light rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-consortium-gold" />
              </div>
              <span className="text-3xl font-bold text-slate-900">98%</span>
            </div>
            <p className="text-slate-600 font-medium">Catalogue Health</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="font-barlow text-2xl font-bold text-slate-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl">
            <button
              onClick={() => navigate('/pdf-import')}
              className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg p-6 hover:from-blue-600 hover:to-blue-700 transition-all shadow-md hover:shadow-lg"
            >
              <div className="flex items-center gap-3 mb-2">
                <FileText className="w-6 h-6" />
                <h3 className="text-lg font-semibold">AI PDF Import</h3>
              </div>
              <p className="text-sm text-blue-50">Upload PDF and extract products with AI</p>
            </button>

            <button
              onClick={() => navigate('/bulk-import')}
              className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-lg p-6 hover:from-green-600 hover:to-green-700 transition-all shadow-md hover:shadow-lg"
            >
              <div className="flex items-center gap-3 mb-2">
                <Upload className="w-6 h-6" />
                <h3 className="text-lg font-semibold">Bulk CSV Import</h3>
              </div>
              <p className="text-sm text-green-50">Upload CSV file to import multiple products at once</p>
            </button>
            
            <button
              onClick={() => navigate('/products/new')}
              className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-lg p-6 hover:from-purple-600 hover:to-purple-700 transition-all shadow-md hover:shadow-lg"
            >
              <div className="flex items-center gap-3 mb-2">
                <Plus className="w-6 h-6" />
                <h3 className="text-lg font-semibold">Add Product</h3>
              </div>
              <p className="text-sm text-purple-50">Add a single product manually</p>
            </button>
          </div>
        </div>

        {dashboardData?.recent_products && dashboardData.recent_products.length > 0 && (
          <div>
            <h2 className="font-barlow text-2xl font-bold text-slate-900 mb-6">Recently Added Products</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {dashboardData.recent_products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
